/**
 * Report category grouping for the reporting landing.
 *
 * A report's `category` is one free-text string that may carry a `<sort:N>`
 * suffix (e.g. `Unified Data <sort:1>`). The suffix is stripped for display and
 * used only to order the groups (no suffix → last; decimals ok). Grouping keys on
 * the stripped display name, so a stray `Unified Data` still merges with
 * `Unified Data <sort:1>`. See QueryViz migration 122.
 */

// Trailing "<sort:N>" (spaces optional, negative + decimals ok), captured group 1.
const SORT_SUFFIX_RE = /\s*<\s*sort\s*:\s*(-?\d+(?:\.\d+)?)\s*>\s*$/i;

export type ParsedCategory = { display: string; sort: number | null };

/** Split a raw category into its display name and optional sort, or null when uncategorized. */
export const parseCategory = (
  raw: string | null | undefined,
): ParsedCategory | null => {
  const s = (raw ?? "").trim();
  if (!s) return null;
  const m = s.match(SORT_SUFFIX_RE);
  if (m && m.index !== undefined) {
    const display = s.slice(0, m.index).trim();
    return { display: display || s, sort: Number.parseFloat(m[1]) };
  }
  return { display: s, sort: null };
};

export type ReportGroup<T> = {
  /** Stable key (lowercased display, or a sentinel for the uncategorized group). */
  key: string;
  /** Display name, or null for the trailing uncategorized group (render no header). */
  label: string | null;
  reports: T[];
};

const UNCATEGORIZED_KEY = " uncategorized";

/**
 * Group reports by category display name, preserving first-appearance order of
 * both groups and rows. Groups are ordered by their sort value (adopted from
 * whichever member first carries one; groups with a sort precede those without),
 * ties break by first appearance, and the uncategorized group always trails.
 *
 * When no report has a category, this returns a single unlabeled group — i.e. the
 * flat list, exactly as before categories existed.
 */
export const groupReportsByCategory = <T>(
  reports: T[],
  getCategory: (r: T) => string | null | undefined,
): ReportGroup<T>[] => {
  type Bucket = {
    label: string | null;
    sort: number | null;
    order: number;
    reports: T[];
  };
  const order: string[] = [];
  const buckets = new Map<string, Bucket>();

  for (const r of reports) {
    const parsed = parseCategory(getCategory(r));
    const key = parsed ? parsed.display.toLowerCase() : UNCATEGORIZED_KEY;
    let b = buckets.get(key);
    if (!b) {
      b = {
        label: parsed ? parsed.display : null,
        sort: parsed ? parsed.sort : null,
        order: order.length,
        reports: [],
      };
      buckets.set(key, b);
      order.push(key);
    } else if (parsed && b.sort === null && parsed.sort !== null) {
      b.sort = parsed.sort; // first member to carry a sort sets the group's order
    }
    b.reports.push(r);
  }

  const isUncat = (key: string) => (key === UNCATEGORIZED_KEY ? 1 : 0);

  return order
    .map((key) => ({ key, ...buckets.get(key)! }))
    .sort((a, b) => {
      const au = isUncat(a.key);
      const bu = isUncat(b.key);
      if (au !== bu) return au - bu; // uncategorized always trails
      const aHas = a.sort !== null;
      const bHas = b.sort !== null;
      if (aHas !== bHas) return aHas ? -1 : 1; // sorted groups before unsorted
      if (aHas && bHas && a.sort !== b.sort) return a.sort! - b.sort!;
      return a.order - b.order; // stable: first appearance
    })
    .map(({ key, label, reports }) => ({ key, label, reports }));
};
